import { RichTextDisplay } from '@tempo-io/fim3-sui-shared';
import { getInitials } from '@tempo-io/string-util';
import { Avatar, AvatarFallback, AvatarImage } from '@tempo-io/sui-avatar';
import { Badge } from '@tempo-io/sui-badge';
import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { ArrowOutward, Dependency } from '@tempo-io/sui-icons';
import { differenceInDays } from 'date-fns';
import { Tooltip as RadixTooltip } from 'radix-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isCustomFieldValueDto } from '@/common/api/types/items';
import { ItemTypeIcon } from '@/common/components/ui/item-type/itemTypeIcon';
import { ItemStatusBadge } from '@/common/components/ui/itemStatusBadge';
import { useGetCustomFieldsQuery } from '@/common/queries/useGetCustomFieldsQuery';
import { useGetItemStatusByIdQuery } from '@/common/queries/useGetItemStatusesQuery';
import { useItemTypesByIdQuery } from '@/common/queries/useItemTypesQuery';
import { DateFormat, formatLocalDateWithFallback } from '@/common/utils/dateFormats';
import { useItemPanelNavigation } from '@/features/item-panel/hooks/useItemPanelParams';
import { TabValue } from '@/features/item-panel/types';
import { useOrgMemberByIdQuery } from '@/features/settings/user-management/queries/useOrgMemberByIdQuery';
import { useGetEstimationsByIds } from '@/features/settings/work-integrations/queries/useGetEstimations';
import { useGetTeamQuery } from '@/features/teams/queries/useGetTeamQuery';
import { usePortalContainer } from '../PortalContainerContext';
import { RADIX_TOOLTIP_CONTENT_BASE } from '../radixBypassStyles';
import { STALE_TIME_MS } from './TimelineItemConstants';
import { TimelineItemTooltipScheduleBar } from './TimelineItemTooltipScheduleBar';
import type { TimelineItemType, TimelineScenarioContext } from './useTimelineTableColumns';

export const getDuration = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): { weeks: number; days: number } | null => {
  if (!startDate || !endDate) return null;
  const diff = differenceInDays(new Date(endDate), new Date(startDate));
  if (diff < 0) return null;
  const totalDays = diff + 1;
  return { weeks: Math.floor(totalDays / 7), days: totalDays % 7 };
};

export const getTooltipAlignOffset = (mouseX: number, resizedWidth: number): number => {
  if (!Number.isFinite(mouseX)) return 0;
  const upperBound = Number.isFinite(resizedWidth) ? Math.max(0, resizedWidth) : 0;
  const clamped = Math.min(Math.max(mouseX, 0), upperBound);
  return Math.min(Math.round(clamped), Math.floor(upperBound));
};

const getAssigneeUserId = (
  item: TimelineItemType,
  userFieldId: number | undefined,
): string | undefined => {
  if (!userFieldId) return undefined;
  const raw = item.customFieldValues?.[String(userFieldId)];
  const entry = Array.isArray(raw) ? raw[0] : raw;
  if (!isCustomFieldValueDto(entry)) return undefined;
  return typeof entry.value === 'string' ? entry.value : undefined;
};

const TimelineItemTooltipAssignee = ({ userId }: { userId: string }) => {
  const { data: user } = useOrgMemberByIdQuery(userId);
  if (!user) return null;
  const displayName = user.name || user.email;
  return (
    <span className="flex items-center justify-end gap-2 min-w-0">
      <Avatar className="size-5 shrink-0">
        <AvatarImage src={user.pictureUrl} alt={displayName} />
        <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
      </Avatar>
      <span className="truncate">{displayName}</span>
    </span>
  );
};

export const TimelineItemTooltip = ({
  item,
  resizedWidth,
  isTooltipOpen,
  isPublishedMode,
  displayStartDate,
  displayEndDate,
  progress,
  hasProgress,
  mouseX,
  showUncertainty,
  scenarioContext,
  barColor,
  barTextColor,
}: {
  item: TimelineItemType;
  resizedWidth: number;
  isTooltipOpen: boolean;
  isPublishedMode: boolean;
  displayStartDate: string | null | undefined;
  displayEndDate: string | null | undefined;
  progress: number | null;
  hasProgress: boolean;
  mouseX: number;
  showUncertainty: boolean;
  scenarioContext?: TimelineScenarioContext;
  barColor?: string;
  barTextColor?: string;
}) => {
  const { t } = useTranslation();
  const portalContainer = usePortalContainer();
  const { openItemPanel } = useItemPanelNavigation();

  const isPlannerMode = showUncertainty && scenarioContext !== undefined;
  const dependencyCount = item.dependencies.length;

  const tooltipAlignOffset = useMemo(
    () => getTooltipAlignOffset(mouseX, resizedWidth),
    [mouseX, resizedWidth],
  );

  const { data: teamData, isLoading: isTeamLoading } = useGetTeamQuery({
    teamId: item.teamId ?? undefined,
    options: {
      staleTime: STALE_TIME_MS,
      enabled: !!item.teamId && isTooltipOpen && !isPublishedMode,
    },
  });
  const { data: estimationsByIds, isLoading: isEstimationSchemeLoading } = useGetEstimationsByIds({
    estimationIds: new Set(item?.estimationId ? [item.estimationId] : []),
    enabled: !!item.estimationId && isTooltipOpen && !isPublishedMode,
    staleTime: STALE_TIME_MS,
  });
  const { data: itemStatuses, isLoading: isItemStatusesLoading } = useGetItemStatusByIdQuery({
    id: item.itemStatusId,
    options: {
      enabled: isTooltipOpen && !!item.itemStatusId && !isPublishedMode,
      staleTime: STALE_TIME_MS,
    },
  });
  const { data: itemTypesById } = useItemTypesByIdQuery({
    enabled: isTooltipOpen && !!item.itemTypeId && !isPublishedMode,
    staleTime: STALE_TIME_MS,
  });
  const itemType = item.itemTypeId ? itemTypesById?.[item.itemTypeId] : undefined;

  const { data: customFields } = useGetCustomFieldsQuery({
    enabled: isTooltipOpen && !isPublishedMode,
    staleTime: STALE_TIME_MS,
  });
  const userFieldId = useMemo(
    () => customFields?.find((field) => field.type === 'USER' && !field.archivedAt)?.id,
    [customFields],
  );
  const assigneeUserId = getAssigneeUserId(item, userFieldId);

  const durationLabel = useMemo(() => {
    const duration = getDuration(displayStartDate, displayEndDate);
    if (!duration) return null;
    const { weeks, days } = duration;
    const weeksLabel =
      weeks > 0
        ? t(($) => $.loopVisualization.timelineTooltip.durationWeeks, { count: weeks })
        : '';
    const daysLabel =
      days > 0 ? t(($) => $.loopVisualization.timelineTooltip.durationDays, { count: days }) : '';
    if (weeksLabel && daysLabel) {
      return t(($) => $.loopVisualization.timelineTooltip.durationWeeksDays, {
        weeks: weeksLabel,
        days: daysLabel,
      });
    }
    return weeksLabel || daysLabel || null;
  }, [displayStartDate, displayEndDate, t]);

  return (
    <RadixTooltip.Portal container={portalContainer ?? undefined}>
      <RadixTooltip.Content
        data-slot="tooltip-content"
        align="start"
        alignOffset={tooltipAlignOffset}
        side="bottom"
        sideOffset={4}
        className={cn(
          RADIX_TOOLTIP_CONTENT_BASE,
          'bg-bg-surface-base text-text-neutral-primary border p-0 min-w-72 max-w-100 w-max max-h-(--radix-tooltip-content-available-height) overflow-y-auto',
        )}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <div className="flex flex-col w-full">
          <div className="flex items-center gap-2 px-4 py-3 w-full">
            {!!itemType && <ItemTypeIcon itemType={itemType} className="shrink-0" />}
            <span className="font-bold text-base flex-1 min-w-0 truncate">{item.name}</span>
            {!!itemType && (
              <Badge variant="secondary" className="shrink-0">
                {itemType.name}
              </Badge>
            )}
            {!isPublishedMode && (
              <div className="flex items-center gap-0.5 shrink-0">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t(($) => $.loopVisualization.timelineTooltip.openItem)}
                  onClick={() => openItemPanel({ itemId: item.id })}
                >
                  <ArrowOutward />
                </Button>
              </div>
            )}
          </div>

          <div className="border-b border-border-neutral" />

          {!!item.description && (
            <>
              <div className="flex flex-col gap-1 px-4 py-3">
                <strong>{t(($) => $.loopVisualization.timelineTooltip.description)}:</strong>
                <RichTextDisplay
                  value={item.description}
                  className="p-0 text-text-neutral-secondary"
                />
              </div>
              <div className="border-b border-border-neutral" />
            </>
          )}

          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 items-center px-4 py-3">
            {!!assigneeUserId && (
              <>
                <strong className="whitespace-nowrap">
                  {t(($) => $.loopVisualization.timelineTooltip.assignedTo)}:
                </strong>
                <span className="flex justify-end min-w-0">
                  <TimelineItemTooltipAssignee userId={assigneeUserId} />
                </span>
              </>
            )}
            {!isEstimationSchemeLoading && estimationsByIds && item.estimationId && (
              <>
                <strong className="whitespace-nowrap">
                  {t(($) => $.common.filters.labels.items.effortEstimate)}:
                </strong>
                <span className="flex min-w-0 justify-end">
                  <Badge variant="secondary" className="min-w-0 max-w-full">
                    <span className="min-w-0 truncate">
                      {estimationsByIds[item.estimationId]?.label}
                    </span>
                  </Badge>
                </span>
              </>
            )}
            {!isTeamLoading && teamData && (
              <>
                <strong className="whitespace-nowrap">
                  {t(($) => $.loopVisualization.layout.headers.fields.team)}:
                </strong>
                <span className="flex min-w-0 justify-end">
                  <Badge variant="secondary" className="min-w-0 max-w-full">
                    <Avatar className="h-4 w-4 shrink-0">
                      <AvatarImage src={teamData.team.icon} alt={teamData.team.name} />
                      <AvatarFallback seed={teamData.team.name}>
                        {teamData.team.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 truncate">{teamData.team.name}</span>
                  </Badge>
                </span>
              </>
            )}
            {!isItemStatusesLoading && itemStatuses && (
              <>
                <strong className="whitespace-nowrap">
                  {t(($) => $.loopVisualization.layout.headers.fields.status)}:
                </strong>
                <span className="flex min-w-0 justify-end">
                  <ItemStatusBadge label={itemStatuses.name} color={itemStatuses.color} />
                </span>
              </>
            )}
          </div>

          <div className="border-b border-border-neutral" />

          {isPlannerMode && scenarioContext ? (
            <TimelineItemTooltipScheduleBar
              itemId={item.id}
              scenarioContext={scenarioContext}
              barColor={barColor}
              barTextColor={barTextColor}
            />
          ) : (
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 items-center px-4 py-3">
              <strong>{t(($) => $.loopVisualization.layout.headers.fields.startDate)}:</strong>
              <span className="text-right">
                {formatLocalDateWithFallback({
                  dateString: displayStartDate,
                  formatStr: DateFormat.MONTH_SHORT_DAY_SHORT_YEAR,
                  fallback: t(($) => $.common.notAvailable),
                })}
              </span>

              <strong>{t(($) => $.loopVisualization.layout.headers.fields.finishDate)}:</strong>
              <span className="text-right">
                {formatLocalDateWithFallback({
                  dateString: displayEndDate,
                  formatStr: DateFormat.MONTH_SHORT_DAY_SHORT_YEAR,
                  fallback: t(($) => $.common.notAvailable),
                })}
              </span>

              {durationLabel !== null && (
                <>
                  <strong>{t(($) => $.loopVisualization.timelineTooltip.duration)}:</strong>
                  <span className="text-right">{durationLabel}</span>
                </>
              )}

              {hasProgress && (
                <>
                  <strong>{t(($) => $.loopVisualization.layout.formatPanel.progressLabel)}:</strong>
                  <span className="text-right">{progress}%</span>
                </>
              )}
            </div>
          )}

          {!isPublishedMode && dependencyCount > 0 && (
            <>
              <div className="border-b border-border-neutral" />
              <div className="flex items-center gap-2 px-4 py-3">
                <Dependency className="shrink-0 text-text-neutral-secondary" />
                <span className="font-bold">
                  {t(($) => $.loopVisualization.timelineTooltip.dependencies)}
                </span>
                <Badge variant="secondary" className="shrink-0">
                  {dependencyCount}
                </Badge>
                <span className="flex-1" />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t(($) => $.loopVisualization.timelineTooltip.openDependencies)}
                  onClick={() =>
                    openItemPanel({ itemId: item.id, panelTab: TabValue.DEPENDENCIES })
                  }
                >
                  <ArrowOutward />
                </Button>
              </div>
            </>
          )}
        </div>
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  );
};
